import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "@/lib/api";
import { formatINR } from "@/data/products";

const Products = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/admin/products")).data,
  });

  const deleteProduct = async (id) => {
    if (!window.confirm("Deactivate this product? It will stop showing on the site.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      toast.success("Product deactivated");
    } catch {
      toast.error("Could not delete product");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-lux text-3xl">Products</h1>
        <Link
          to="/admin/products/new"
          className="flex items-center gap-2 bg-[#111111] text-white rounded-full px-5 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-[#C07C5F] transition-colors"
        >
          <Plus size={14} /> Add Product
        </Link>
      </div>

      {isLoading ? null : (
        <div className="border border-[#EBE8E3] rounded-lg overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] text-[#5C5C5C] text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-3">Product</th>
                <th className="text-left px-5 py-3">Price</th>
                <th className="text-left px-5 py-3">Stock</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EBE8E3]">
              {(data?.products || []).map((p) => (
                <tr key={p._id}>
                  <td className="px-5 py-3 flex items-center gap-3">
                    {p.image && <img src={resolveImage(p.image)} alt={p.name} className="w-10 h-10 object-cover" />}
                    <div>
                      <p className="font-medium">{p.name}</p>
                      <p className="text-xs text-[#5C5C5C]">{p.slug}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3">{formatINR(p.price)}</td>
                  <td className="px-5 py-3">{p.stock}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full ${p.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-3">
                      <Link to={`/admin/products/${p._id}/edit`} className="text-[#5C5C5C] hover:text-[#111111]">
                        <Pencil size={16} />
                      </Link>
                      <button onClick={() => deleteProduct(p._id)} className="text-[#5C5C5C] hover:text-red-600">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Products;
